import React from 'react';
import { Mail, Phone, MapPin, FileText, Calendar, User, Megaphone } from 'lucide-react';

// Display the raw "key: value" content of a Meta / Google / TikTok / web
// form note as a structured card: contact header, question/answer list,
// and a small metadata footer. Works retroactively on existing notes
// because parsing is done on render, not at capture time.

const CONTACT_KEYS = new Set([
  'full_name', 'first_name', 'last_name', 'name',
  'email', 'user_email', 'email_address', 'work_email',
  'phone', 'phone_number', 'user_phone', 'work_phone',
  'user_provided_phone_number',
  'city', 'state', 'province', 'country',
  'postal_code', 'zip_code', 'zipcode',
  'company', 'company_name', 'business_name',
  'job_title', 'job_role',
]);

// Lines we never want to surface — either empty placeholders Meta inserts,
// or technical IDs we already render in the footer.
const SKIP_KEYS = new Set([
  'inbox_url',
  'meta ad_id', 'leadgen_id', 'ad_id',
  'google_lead_id', 'google_campaign_id', 'google_form_id',
  'google_adgroup_id', 'google_creative_id', 'google_api_version', 'gclid',
  'tiktok_lead_id', 'tiktok_page_id',
]);

function prettify(s) {
  if (s == null) return '';
  const t = String(s).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// Strip the trailing ? from snake_case question keys before prettifying,
// then re-attach so the rendered label keeps its question mark.
function prettyQuestion(k) {
  const raw = String(k || '').trim();
  const hadQ = raw.endsWith('?');
  const body = hadQ ? raw.slice(0, -1) : raw;
  const pretty = prettify(body);
  return hadQ ? pretty + '?' : pretty;
}

// Split content into contact fields, custom Q/A pairs, and footer metadata.
// Footer lines look like "Form: X · leadgen_id: Y · ad_id: Z · submitted: ISO"
// and the various inboundWebhooks handlers all emit a single trailing line
// of this shape, so we recognise it on the `·` separator.
function parseContent(content) {
  const out = { contact: {}, questions: [], footer: {}, freeText: [], attribution: null };
  if (!content) return out;

  for (const rawLine of String(content).split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    // Attribution recap emitted by the capture paths:
    //   "Campaign: X · Ad set: Y · Ad: Z"  /  "Organic (form shared without an ad)"
    if (/^(Campaign|Ad set|Ad):\s/.test(line)) {
      const attr = {};
      for (const seg of line.split(' · ')) {
        const mm = seg.match(/^(Campaign|Ad set|Ad):\s*(.+)$/);
        if (mm) attr[mm[1] === 'Ad set' ? 'adset' : mm[1].toLowerCase()] = mm[2].trim();
      }
      if (Object.keys(attr).length) { out.attribution = attr; continue; }
    }
    if (/^Organic \(form shared without an ad\)$/i.test(line)) { out.attribution = { organic: true }; continue; }

    // Trailing metadata line(s).
    if (line.includes('leadgen_id:') || line.includes('lead_id:') || /^Form:\s/i.test(line)) {
      const formMatch = line.match(/Form:\s*([^·]+?)(?:\s*·|$)/i);
      const leadgenMatch = line.match(/leadgen_id:\s*(\S+)/i);
      const adMatch = line.match(/ad_id:\s*(\S+)/i);
      const subMatch = line.match(/submitted:\s*(\S+)/i);
      if (formMatch && !out.footer.form) out.footer.form = formMatch[1].trim();
      if (leadgenMatch) out.footer.leadgenId = leadgenMatch[1];
      if (adMatch && adMatch[1] !== 'unknown') out.footer.adId = adMatch[1];
      if (subMatch) out.footer.submittedAt = subMatch[1];
      continue;
    }

    // Lines that don't fit the "key: value" shape are free text (e.g. a
    // plain webform/n8n message) — keep them so the card never renders
    // an empty shell for unstructured content.
    const m = line.match(/^([^:]+):\s*(.*)$/);
    if (!m) { out.freeText.push(line); continue; }
    const rawKey = m[1].trim();
    // Normalise key so legacy ("Phone number", "Full name") and new
    // snake_case ("phone_number", "full_name") both hit the contact set.
    const normKey = rawKey
      .toLowerCase()
      .replace(/\?+$/, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_');
    const val = m[2].trim();
    if (!val) continue;
    if (SKIP_KEYS.has(normKey)) continue;

    if (CONTACT_KEYS.has(normKey)) {
      // Don't store user_provided_phone_number if we already have phone
      // with the same value — Meta sends it as a duplicate field.
      if (normKey === 'user_provided_phone_number' && out.contact.phone === val) continue;
      out.contact[normKey] = val;
    } else {
      out.questions.push({ key: rawKey, val });
    }
  }
  return out;
}

const MetaLeadFormCard = ({
  content,
  formName,        // optional explicit override (e.g. from lead.customFields.meta_form_name)
  leadgenId,       // optional override
  submittedAt,     // optional override (Date or ISO string)
  className = '',
  // FCFS hook — caller can intercept the phone tap and run a claim
  // guard before letting the dial happen. Card itself has no lead
  // context, so the caller (which does) decides what to do.
  onPhoneClick,
  // Same idea for the email link — clicking it launches the external
  // mail client, which is an off-platform contact attempt the caller
  // can gate behind a claim.
  onEmailClick,
}) => {
  const { contact, questions, footer, freeText, attribution } = parseContent(content);

  const displayName =
    contact.full_name ||
    [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() ||
    contact.name ||
    null;
  const email = contact.email || contact.user_email || contact.email_address || contact.work_email;
  const phone =
    contact.phone || contact.phone_number || contact.user_phone || contact.work_phone ||
    contact.user_provided_phone_number;
  const location = [contact.city, contact.state || contact.province, contact.postal_code || contact.zip_code]
    .filter(Boolean).join(', ');
  const company = contact.company || contact.company_name || contact.business_name;

  const finalForm = formName || footer.form;
  // Prefer the leadgen_id parsed out of the content (per-submission,
  // accurate when a lead has multiple form submissions). Fall back to
  // the prop the consumer passed in, which is typically derived from
  // the lead's meta:leadgen:* tag so messenger fallback cards still
  // show a reference id when the leadgen webhook also fired.
  const finalLeadgen = footer.leadgenId || leadgenId;
  const finalSubmitted = submittedAt || footer.submittedAt;

  return (
    <div className={`rounded-lg border border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white overflow-hidden ${className}`}>
      {(displayName || email || phone || location || company) && (
        <div className="px-4 py-3 border-b border-indigo-100/70">
          {displayName && (
            <div className="flex items-center gap-2 mb-1.5">
              <User size={14} className="text-indigo-500" />
              <span className="text-sm font-semibold text-gray-900">{displayName}</span>
            </div>
          )}
          <div className="space-y-1 text-xs text-gray-700">
            {email && (
              <div className="flex items-center gap-2">
                <Mail size={11} className="text-gray-400 flex-shrink-0" />
                <a
                  href={`mailto:${email}`}
                  className="hover:text-indigo-700 break-all"
                  onClick={onEmailClick ? (e) => onEmailClick(email, e) : undefined}
                >
                  {email}
                </a>
              </div>
            )}
            {phone && (
              <div className="flex items-center gap-2">
                <Phone size={11} className="text-gray-400 flex-shrink-0" />
                <a
                  href={`tel:${phone}`}
                  className="hover:text-indigo-700"
                  onClick={onPhoneClick ? (e) => onPhoneClick(phone, e) : undefined}
                >
                  {phone}
                </a>
              </div>
            )}
            {location && (
              <div className="flex items-center gap-2">
                <MapPin size={11} className="text-gray-400 flex-shrink-0" />
                <span>{location}</span>
              </div>
            )}
            {company && (
              <div className="flex items-center gap-2">
                <FileText size={11} className="text-gray-400 flex-shrink-0" />
                <span>{company}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {freeText.length > 0 && (
        <div className="px-4 py-3 text-sm text-gray-900 whitespace-pre-wrap" style={{ wordBreak: 'break-word', lineHeight: '1.5' }}>
          {freeText.join('\n')}
        </div>
      )}

      {questions.length > 0 && (
        <div className="px-4 py-3 space-y-2.5">
          {questions.map((q, i) => (
            <div key={i}>
              <div className="text-[11px] font-medium text-gray-500 leading-tight">
                {prettyQuestion(q.key)}
              </div>
              <div className="text-sm text-gray-900 mt-0.5 break-words">
                {prettify(q.val)}
              </div>
            </div>
          ))}
        </div>
      )}

      {attribution && (
        <div className="px-4 py-2 border-t border-indigo-100/70 bg-indigo-50/40 text-[11px] text-gray-700 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <Megaphone size={11} className="text-indigo-500 shrink-0" />
          {attribution.organic ? (
            <span>Organic — form shared without an ad</span>
          ) : (
            [attribution.campaign, attribution.adset, attribution.ad].filter(Boolean).map((label, i, arr) => (
              <React.Fragment key={`${i}-${label}`}>
                <span className={i === arr.length - 1 ? 'font-medium text-gray-900' : ''}>{label}</span>
                {i < arr.length - 1 && <span className="text-gray-300">›</span>}
              </React.Fragment>
            ))
          )}
        </div>
      )}
      {(finalForm || finalSubmitted || finalLeadgen) && (
        <div className="px-4 py-2 border-t border-indigo-100/70 bg-indigo-50/30 text-[10.5px] text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
          {finalForm && (
            <span className="flex items-center gap-1">
              <FileText size={10} />
              {finalForm}
            </span>
          )}
          {finalSubmitted && (
            <span className="flex items-center gap-1">
              <Calendar size={10} />
              {(() => {
                try { return new Date(finalSubmitted).toLocaleString(); }
                catch { return String(finalSubmitted); }
              })()}
            </span>
          )}
          {finalLeadgen && (
            <span className="font-mono text-gray-400">#{finalLeadgen}</span>
          )}
        </div>
      )}
    </div>
  );
};

// Sources whose note content is shaped like "key: value\n..." and should
// render through MetaLeadFormCard instead of the default text renderer.
export const FORM_NOTE_SOURCES = new Set(['meta_ad', 'google_ad', 'tiktok_ad', 'webform']);

// Click-to-Messenger / Click-to-IG ads embed the lead form contents in
// the first DM, so we get a messenger/instagram channel message whose
// body is actually a form submission. Detect by shape: at least 3
// "Key: Value" lines with a recognised contact field. Regular DM text
// won't trigger this — humans don't write "Email: …\nPhone: …" by hand.
export function looksLikeFormSubmission(content) {
  if (!content || typeof content !== 'string') return false;
  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 3) return false;
  let kvCount = 0;
  let hasContact = false;
  for (const line of lines) {
    const m = line.match(/^([^:]{1,120}):\s*(.+)$/);
    if (!m) continue;
    kvCount++;
    const normKey = m[1].trim()
      .toLowerCase()
      .replace(/\?+$/, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_');
    if (CONTACT_KEYS.has(normKey)) hasContact = true;
  }
  return kvCount >= 3 && hasContact;
}

export default MetaLeadFormCard;
